import React from 'react'
import styled, { css } from 'styled-components'
import { Block, Grid, H3, H4, P } from '@mote/ui'

const TYPES = {
  MAIN: 'main',
  SECONDARY: 'secondary'
}

export const Section = props => {
  const {
    className,
    title,
    subtitle,
    surtitle,
    type = 'main',
    variant = 'grid',
    noPadding = false,
    children,
    ...rest
  } = props

  const { Wrapper, wrapperProps } = React.useMemo(() => {
    const defaultProps = {
      className: type === TYPES.SECONDARY ? 'section-content' : 'main-content'
    }
    const { rows = 1, columns = 1, ...restProps } = rest

    switch (variant) {
      case 'grid': {
        return {
          Wrapper: StyledGrid,
          wrapperProps: { ...defaultProps, rows, columns, ...restProps }
        }
      }
      case 'flex': {
        return {
          Wrapper: StyledBlock,
          wrapperProps: { ...defaultProps, ...restProps }
        }
      }
      default:
        return { Wrapper: React.Fragment, wrapperProps: {} }
    }
  }, [variant, type, rest])
  const textProps = React.useMemo(() => {
    const surtitle = {
      fontSize: 13,
      lineHeight: '120%',
      fontWeight: 400,
      color: '#25252D'
    }
    const subtitle = {
      fontSize: 15,
      fontWeight: 400,
      lineHeight: '120%',
      color: '#6C6C84'
    }
    let title = {
      fontSize: 24,
      fontWeight: 500,
      lineHeight: '160%',
      color: '#25252D'
    }

    if (type === TYPES.SECONDARY) {
      title = {
        ...title,
        fontSize: 18,
        lineHeight: '140%'
      }
    }

    return {
      title: { ...title, ...(rest.titleProps ?? {}) },
      subtitle: { ...subtitle, ...(rest.subtitleProps ?? {}) },
      surtitle: { ...surtitle, ...(rest.surtitleProps ?? {}) }
    }
  }, [type, rest])

  return (
    <Container
      as='section'
      className={className}
      layout='vertical'
      gap={32}
      noPadding={noPadding}
      spreadX
      {...(rest.containerProps ?? {})}
    >
      {title && (
        <TitleContainer
          layout='vertical'
          align='flex-start'
          gap={8}
          {...(rest.titleContainerProps ?? {})}
        >
          {surtitle && <Surtitle {...textProps.surtitle}>{surtitle}</Surtitle>}
          <Title {...textProps.title}>{title}</Title>
          {subtitle && <Subtitle {...textProps.subtitle}>{subtitle}</Subtitle>}
        </TitleContainer>
      )}
      <Wrapper {...wrapperProps}>{children}</Wrapper>
    </Container>
  )
}

const Container = styled(Block)`
  ${({ noPadding }) =>
    !noPadding &&
    css`
      padding: 24px 0;

      @media (max-width: 639px) {
        padding-bottom: 64px;
      }
    `};
`

const StyledGrid = styled(Grid)`
  ${({ gap, gapX, gapY }) =>
    gap === undefined &&
    gapX === undefined &&
    gapY === undefined &&
    css`
      gap: 24px;
    `};

  ${({ columns }) =>
    columns !== 1 &&
    css`
      @media (max-width: 991px) {
        grid-template-columns: repeat(2, minmax(0, 1fr));
      }
    `}

  @media (max-width: 639px) {
    grid-template-columns: 100%;
    grid-template-rows: unset;
    gap: 16px;
  }
`

const StyledBlock = styled(Block)`
  @media (max-width: 639px) {
    flex-direction: column !important;
  }

  ${({ gap }) =>
    gap === undefined &&
    css`
      gap: 24px;

      @media (max-width: 639px) {
        gap: 16px;
      }
    `};
`

const TitleContainer = styled(Block)``
const Title = styled(H3)``
const Subtitle = styled(H4)`
  max-width: 500px;
`
const Surtitle = styled(P)``
